import { ApiError, type AppVersionRow, type AppVersionSaveRequest } from '@hq/api-sdk';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  Upload
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { sumWidth, TipText } from '@hq/ui-kit';
import { buildAppsUrl, deleteAppFile, isLoopbackUrl, uploadAppFile } from '../appsStore';
import { hq } from '../session';

interface FormValues {
  appKey: string;
  appName: string;
  packageName?: string;
  androidVersion?: string;
  iosVersion?: string;
  downloadHost?: string;
  iosDownloadUrl?: string;
  iosLocalDownloadUrl?: string;
  enabled: boolean;
  remark?: string;
}

/**
 * App 版本配置（sys_app_version）：每个 App 一条，维护包名/当前版本号/下载域名/logo。
 * 参考旧项目 hsmsx_webf #/setting/version。安装包发布在「App上传管理」页维护。
 * 发布物（apk / 二维码 / logo 图片）均由本站点本地存到「apps 目录」（不经过后端），下载走同源 /apps/*。
 */
export default function AppVersions() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState('');
  const [editing, setEditing] = useState<AppVersionRow | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<FormValues>();
  // logo 上传后的回填值（保存时随表单一起提交）
  const [logo, setLogo] = useState<{ fileName: string; url?: string | null } | null>(null);

  const query = useQuery({
    queryKey: ['app-versions', keyword],
    queryFn: () => hq.appVersion.version.list(keyword)
  });
  const rows = query.data ?? [];
  const loading = query.isLoading || query.isFetching;

  const load = () => queryClient.invalidateQueries({ queryKey: ['app-versions'] });

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue({ enabled: true });
    setLogo(null);
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (row: AppVersionRow) => {
    setEditing(row);
    setLogo(row.logoFileName ? { fileName: row.logoFileName, url: row.logoUrl } : null);
    form.setFieldsValue({
      appKey: row.appKey,
      appName: row.appName,
      packageName: row.packageName ?? undefined,
      androidVersion: row.androidVersion ?? undefined,
      iosVersion: row.iosVersion ?? undefined,
      downloadHost: row.downloadHost ?? undefined,
      iosDownloadUrl: row.iosDownloadUrl ?? undefined,
      iosLocalDownloadUrl: row.iosLocalDownloadUrl ?? undefined,
      enabled: row.enabled === 1,
      remark: row.remark ?? undefined
    });
    setOpen(true);
  };

  const submit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const body: AppVersionSaveRequest = {
        appKey: values.appKey.trim(),
        appName: values.appName.trim(),
        packageName: values.packageName?.trim() || null,
        androidVersion: values.androidVersion?.trim() || null,
        iosVersion: values.iosVersion?.trim() || null,
        downloadHost: values.downloadHost?.trim() || null,
        iosDownloadUrl: values.iosDownloadUrl?.trim() || null,
        iosLocalDownloadUrl: values.iosLocalDownloadUrl?.trim() || null,
        logoFileName: logo?.fileName ?? null,
        logoUrl: logo?.url ?? null,
        enabled: values.enabled,
        remark: values.remark?.trim() || null
      };
      await hq.appVersion.version.save(body);
      // logo 换过就清掉旧文件，避免 apps 目录堆积孤儿图片
      if (editing?.logoFileName && editing.logoFileName !== (body.logoFileName ?? null)) {
        await deleteAppFile(editing.logoFileName);
      }
      message.success(editing ? '已更新版本配置' : '已创建版本配置');
      setOpen(false);
      await load();
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : String(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file: File) => {
    try {
      // logo 文件直接存到站点本地 apps 目录（不经过后端），地址按当前下载域名/当前 origin 拼绝对值
      const saved = await uploadAppFile(file, file.name);
      const host = form.getFieldValue('downloadHost') as string | undefined;
      const url = buildAppsUrl(saved.fileName, host);
      setLogo({ fileName: saved.fileName, url });
      if (isLoopbackUrl(url)) {
        message.warning('未配置下载对外域名，logo 地址按本机 localhost 落库，其他终端会显示不出图片');
      }
      message.success('logo 已保存到站点本地 apps 目录');
    } catch (err) {
      message.error(err instanceof Error ? err.message : String(err));
    }
    return false; // 阻止 antd 自动上传
  };

  const remove = async (row: AppVersionRow) => {
    try {
      await hq.appVersion.version.remove(row.id);
      // 后端只软删配置记录，logo 实体文件由站点本地通道清理（尽力而为）
      if (row.logoFileName) await deleteAppFile(row.logoFileName);
      message.success('已删除版本配置');
      await load();
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : String(err));
    }
  };

  const columns: ColumnsType<AppVersionRow> = [
    { title: 'AppKey', dataIndex: 'appKey', width: 160, ellipsis: { showTitle: false }, render: (v: string) => <TipText text={v} /> },
    { title: 'App 名称', dataIndex: 'appName', width: 160, ellipsis: { showTitle: false }, render: (v: string) => <TipText text={v} /> },
    { title: '包名', dataIndex: 'packageName', width: 180, ellipsis: { showTitle: false }, render: (v: string | null) => (v ? <TipText text={v} /> : '-') },
    { title: '安卓版本', dataIndex: 'androidVersion', width: 110, render: (v: string | null) => v ?? '-' },
    { title: 'iOS 版本', dataIndex: 'iosVersion', width: 110, render: (v: string | null) => v ?? '-' },
    {
      title: 'Logo',
      dataIndex: 'logoUrl',
      width: 72,
      render: (v: string | null) => (v ? <img src={v} alt="logo" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8 }} /> : '-')
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      width: 80,
      render: (v: number) => (v === 1 ? <Tag color="green">启用</Tag> : <Tag>下架</Tag>)
    },
    {
      title: '操作',
      key: 'action',
      width: 130,
      fixed: 'right',
      render: (_v, row) => (
        <Space size={4}>
          <Button size="small" onClick={() => openEdit(row)}>
            编辑
          </Button>
          <Popconfirm title="删除版本配置（不删除已上传发布），确认？" onConfirm={() => void remove(row)}>
            <Button size="small" danger type="link">
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card
      title="App 版本管理"
      extra={
        <Space size={8}>
          <Input.Search
            allowClear
            placeholder="搜索 AppKey / 名称"
            style={{ width: 220 }}
            onSearch={(v) => setKeyword(v)}
          />
          <Button onClick={() => void load()} loading={loading}>
            刷新
          </Button>
          <Button type="primary" onClick={openCreate}>
            新建 App
          </Button>
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="每个 App 一条配置；当前生效版本号以「App上传管理」的当前发布为准，此处作为端侧回退与展示口径。安装包 / 二维码 / logo 均落本站点根「apps 目录」。"
      />
      <Table<AppVersionRow>
        size="small"
        rowKey="id"
        loading={loading}
        pagination={false}
        dataSource={rows}
        scroll={{ x: sumWidth(columns) }}
        columns={columns}
      />
      {query.error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 12 }}
          message={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Modal
        title={editing ? `编辑：${editing.appName}` : '新建 App'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => void submit()}
        confirmLoading={saving}
        width={640}
        destroyOnHidden
      >
        <Form<FormValues> form={form} layout="vertical">
          <Form.Item
            name="appKey"
            label="AppKey"
            rules={[{ required: true, message: '请输入 AppKey' }]}
            extra="端侧检查更新上送的标识（如包名/机构短码），创建后不可改"
          >
            <Input maxLength={64} disabled={Boolean(editing)} placeholder="如 hsmsx.appw" />
          </Form.Item>
          <Form.Item name="appName" label="App 名称" rules={[{ required: true, message: '请输入 App 名称' }]}>
            <Input maxLength={128} placeholder="如 物资平台 App" />
          </Form.Item>
          <Form.Item name="packageName" label="包名 / bundleId">
            <Input maxLength={128} placeholder="如 com.hsmsx.wuzi" />
          </Form.Item>
          <Space style={{ width: '100%' }} size={12}>
            <Form.Item name="androidVersion" label="当前安卓版本" style={{ flex: 1, width: '50%' }}>
              <Input maxLength={32} placeholder="如 1.2.3" />
            </Form.Item>
            <Form.Item name="iosVersion" label="当前 iOS 版本" style={{ flex: 1, width: '50%' }}>
              <Input maxLength={32} placeholder="如 1.2.3" />
            </Form.Item>
          </Space>
          <Form.Item name="downloadHost" label="下载对外域名" extra="选填，覆盖全局配置；留空按请求来源兜底">
            <Input maxLength={255} placeholder="如 https://dl.xxx.com" />
          </Form.Item>
          <Form.Item name="iosDownloadUrl" label="iOS AppStore 地址">
            <Input maxLength={500} placeholder="https://apps.apple.com/..." />
          </Form.Item>
          <Form.Item name="iosLocalDownloadUrl" label="iOS 企业分发地址（itms-services）">
            <Input maxLength={500} />
          </Form.Item>
          <Form.Item label="Logo" extra="上传后写入 apps 目录，仅支持图片">
            <Space align="center">
              <Upload accept="image/*" showUploadList={false} beforeUpload={(f) => void uploadLogo(f)}>
                <Button>选择图片</Button>
              </Upload>
              {logo?.url ? (
                <img src={logo.url} alt="logo" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8 }} />
              ) : (
                <Typography.Text type="secondary">未选择</Typography.Text>
              )}
            </Space>
          </Form.Item>
          <Form.Item name="enabled" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={200} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
